/**
 * SPA hooks for keepa_search_runs — Pass 5 wiring.
 *
 * Surfaces:
 *   - useKeepaSearchRuns({ status }) → /runs page list
 *   - useCreateKeepaSearchRun()      → search page "Queue" / "Run now"
 *   - useCancelKeepaSearchRun()      → per-row Cancel action
 *   - useRetryKeepaSearchRun()       → per-row Retry action
 *   - useKeepaBudget()               → pre-flight budget hint
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateKeepaSearchRunRequest,
  KeepaBudgetResponse,
  KeepaSearchRunResponse,
  ListKeepaSearchRunsResponse,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export interface KeepaSearchRunsParams {
  /** Comma-separated statuses; e.g. "queued,running". */
  status?: string;
  limit?: number;
}

export function keepaSearchRunsQuery(params: KeepaSearchRunsParams = {}) {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path = `/api/keepa-search-runs${qs ? `?${qs}` : ''}`;

  return queryOptions({
    queryKey: ['keepa-search-runs', params.status ?? '', params.limit ?? 25] as const,
    queryFn: () =>
      apiFetch<ListKeepaSearchRunsResponse>(path, {
        responseSchema: ListKeepaSearchRunsResponse,
      }),
    // Run rows transition fast (queued → running → succeeded in <2min once
    // the sweep fires), so a 5s staleness keeps the page snappy.
    staleTime: 5_000,
  });
}

export function useKeepaSearchRuns(params: KeepaSearchRunsParams = {}) {
  return useQuery(keepaSearchRunsQuery(params));
}

export function useCreateKeepaSearchRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKeepaSearchRunRequest) =>
      apiFetch<KeepaSearchRunResponse>('/api/keepa-search-runs', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: KeepaSearchRunResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-search-runs'] });
    },
  });
}

export function useCancelKeepaSearchRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<KeepaSearchRunResponse>(`/api/keepa-search-runs/${id}/cancel`, {
        method: 'POST',
        responseSchema: KeepaSearchRunResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-search-runs'] });
    },
  });
}

export function useRetryKeepaSearchRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<KeepaSearchRunResponse>(`/api/keepa-search-runs/${id}/retry`, {
        method: 'POST',
        responseSchema: KeepaSearchRunResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-search-runs'] });
    },
  });
}

/**
 * Pre-flight budget query: returns the same decision shape the cron sweep
 * uses. The SPA renders an inline warning when ok=false, suggesting the
 * user queue instead of run-now.
 */
export function useKeepaBudget(estimatedTokens?: number) {
  const search = new URLSearchParams();
  if (estimatedTokens !== undefined) search.set('estimatedTokens', String(estimatedTokens));
  const qs = search.toString();
  const path = `/api/keepa-search-runs/budget${qs ? `?${qs}` : ''}`;

  return useQuery({
    queryKey: ['keepa-budget', estimatedTokens ?? null] as const,
    queryFn: () => apiFetch<KeepaBudgetResponse>(path, { responseSchema: KeepaBudgetResponse }),
    // Same staleness as the live balance chip — recompute whenever the
    // search form re-mounts but don't poll.
    staleTime: 0,
    retry: false,
  });
}
